import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MessageSquare, Building2, CreditCard, ShieldAlert,
  Globe, ChevronRight, Share2, Save, Pencil, X, Trash2,
} from 'lucide-react';
import { useLeadDetail, useUpdateNotes, useDeleteLead, useUpdateLead } from '@/hooks/useLeads';
import { Card, LeadScoreBadge, RiskBadge, Badge, Button, SkeletonCard, CountryCombobox } from '@/components/ui';
import { shareLead } from '@/utils/share';
import { useT, useLanguage } from '@/i18n/LanguageContext';
import { useTranslateContent } from '@/i18n/translateContent';

export default function LeadDetailPage() {
  const t = useT();
  const { lang } = useLanguage();
  const tr = useTranslateContent(lang);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: lead, isLoading } = useLeadDetail(id);
  const updateNotes = useUpdateNotes();
  const deleteLead = useDeleteLead();
  const updateLead = useUpdateLead();

  const [notes, setNotes] = useState('');
  const [notesEdited, setNotesEdited] = useState(false);

  // Edit mode state
  const [editingContact, setEditingContact] = useState(false);
  const [editingEntity, setEditingEntity] = useState(false);
  const [contactForm, setContactForm] = useState({ fullName: '', email: '', phone: '', title: '' });
  const [entityForm, setEntityForm] = useState({ companyName: '', country: '', website: '' });

  useEffect(() => {
    if (lead?.notes) {
      setNotes(lead.notes);
    }
  }, [lead?.notes]);

  // Sync forms when lead data loads
  useEffect(() => {
    if (lead) {
      setContactForm({
        fullName: lead.contact.fullName,
        email: lead.contact.email,
        phone: lead.contact.phone,
        title: lead.contact.title,
      });
      setEntityForm({
        companyName: lead.entity.companyName,
        country: lead.entity.country,
        website: lead.entity.website,
      });
    }
  }, [lead]);

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-4 md:p-6 text-center">
        <p className="text-reevol-text-muted">{t.leadDetail.leadNotFound}</p>
        <Button variant="ghost" onClick={() => navigate('/leads')} className="mt-4">
          {t.common.backToLeads}
        </Button>
      </div>
    );
  }

  const handleSaveNotes = () => {
    if (!id) return;
    updateNotes.mutate({ leadId: id, notes });
    setNotesEdited(false);
  };

  const handleShare = async () => {
    await shareLead({
      title: `${lead.entity.companyName} - Lead Insight`,
      text: `${lead.contact.fullName} at ${lead.entity.companyName} — Score: ${lead.scoreBand} (${lead.scoreNumeric})`,
      url: window.location.href,
    });
  };

  const handleDelete = () => {
    if (!id) return;
    if (window.confirm(t.leadDetail.confirmDelete)) {
      deleteLead.mutate(id, {
        onSuccess: () => navigate('/leads', { replace: true }),
      });
    }
  };

  const handleSaveContact = () => {
    if (!id) return;
    updateLead.mutate({ leadId: id, contact: contactForm }, {
      onSuccess: () => setEditingContact(false),
    });
  };

  const handleCancelContact = () => {
    setContactForm({
      fullName: lead.contact.fullName,
      email: lead.contact.email,
      phone: lead.contact.phone,
      title: lead.contact.title,
    });
    setEditingContact(false);
  };

  const handleSaveEntity = () => {
    if (!id) return;
    updateLead.mutate({ leadId: id, entity: entityForm }, {
      onSuccess: () => setEditingEntity(false),
    });
  };

  const handleCancelEntity = () => {
    setEntityForm({
      companyName: lead.entity.companyName,
      country: lead.entity.country,
      website: lead.entity.website,
    });
    setEditingEntity(false);
  };

  const statusVariant = lead.entity.status === 'active' ? 'success' : lead.entity.status === 'dissolved' ? 'danger' : 'neutral';

  return (
    <div className="p-4 md:p-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/leads')}
        className="flex items-center gap-1 text-sm text-reevol-text-muted hover:text-reevol-blue mb-4 transition-colors"
      >
        <ArrowLeft size={16} />
        {t.common.backToLeads}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Header / Contact Info */}
          <Card className="p-5">
            <div className="flex items-start justify-between gap-4">
              {editingContact ? (
                <div className="flex-1 space-y-2">
                  <EditField label={t.leadDetail.fullName} value={contactForm.fullName} onChange={(v) => setContactForm((p) => ({ ...p, fullName: v }))} />
                  <EditField label={t.leadDetail.title} value={contactForm.title} onChange={(v) => setContactForm((p) => ({ ...p, title: v }))} />
                  <EditField label={t.leadDetail.email} value={contactForm.email} onChange={(v) => setContactForm((p) => ({ ...p, email: v }))} />
                  <EditField label={t.leadDetail.phone} value={contactForm.phone} onChange={(v) => setContactForm((p) => ({ ...p, phone: v }))} />
                  <div className="flex gap-2 pt-1">
                    <Button onClick={handleSaveContact} loading={updateLead.isPending} icon={<Save size={14} />}>
                      {t.common.save}
                    </Button>
                    <button onClick={handleCancelContact} className="px-3 py-1.5 text-sm text-reevol-text-muted hover:text-reevol-text transition-colors">
                      {t.common.cancel}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <h1 className="text-xl font-bold text-reevol-text">{lead.entity.companyName}</h1>
                  <p className="text-sm text-reevol-text-secondary mt-1">
                    {lead.contact.fullName} · {lead.contact.title}
                  </p>
                  {lead.contact.email && (
                    <a href={`mailto:${lead.contact.email}`} className="text-sm text-reevol-blue hover:underline">
                      {lead.contact.email}
                    </a>
                  )}
                  {lead.contact.phone && (
                    <p className="text-sm text-reevol-text-secondary">{lead.contact.phone}</p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge variant={statusVariant}>{tr.status(lead.entity.status) || t.common.unknown}</Badge>
                {!editingContact && (
                  <button onClick={() => setEditingContact(true)} className="p-2 rounded-xl hover:bg-gray-100 text-reevol-text-muted" title={t.leadDetail.editContact}>
                    <Pencil size={14} />
                  </button>
                )}
                <button onClick={handleShare} className="p-2 rounded-xl hover:bg-gray-100 text-reevol-text-muted">
                  <Share2 size={16} />
                </button>
              </div>
            </div>
          </Card>

          {/* AI Summary */}
          <Card className="p-5" accentColor="#4D3AE9">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={16} className="text-reevol-blue" />
              <h2 className="text-sm font-semibold text-reevol-text">{t.leadDetail.aiSummary}</h2>
            </div>
            <p className="text-sm text-reevol-text-secondary leading-relaxed">{lang === 'zh' && lead.aiSummaryZh ? lead.aiSummaryZh : lead.aiSummary}</p>
          </Card>

          {/* Company Profile */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-reevol-text-muted" />
                <h2 className="text-sm font-semibold text-reevol-text">{t.leadDetail.companyProfile}</h2>
              </div>
              {!editingEntity && (
                <button onClick={() => setEditingEntity(true)} className="p-1.5 rounded-lg hover:bg-gray-100 text-reevol-text-muted" title={t.leadDetail.editCompany}>
                  <Pencil size={14} />
                </button>
              )}
            </div>

            {editingEntity ? (
              <div className="space-y-2">
                <EditField label={t.leadDetail.companyName} value={entityForm.companyName} onChange={(v) => setEntityForm((p) => ({ ...p, companyName: v }))} />
                <CountryCombobox label={t.leadDetail.country} value={entityForm.country} onChange={(v) => setEntityForm((p) => ({ ...p, country: v }))} />
                <EditField label={t.leadDetail.website} value={entityForm.website} onChange={(v) => setEntityForm((p) => ({ ...p, website: v }))} />
                <div className="flex gap-2 pt-1">
                  <Button onClick={handleSaveEntity} loading={updateLead.isPending} icon={<Save size={14} />}>
                    {t.common.save}
                  </Button>
                  <button onClick={handleCancelEntity} className="px-3 py-1.5 text-sm text-reevol-text-muted hover:text-reevol-text transition-colors">
                    {t.common.cancel}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                  <InfoRow label={t.leadDetail.country} value={tr.country(lead.entity.country)} />
                  <InfoRow label={t.leadDetail.incorporation} value={lead.entity.incorporationYear ? String(lead.entity.incorporationYear) : null} />
                  <InfoRow label={t.leadDetail.employees} value={lead.entity.employees} />
                  <InfoRow label={t.leadDetail.revenue} value={lead.entity.revenue} />
                  <InfoRow label={t.leadDetail.industry} value={tr.industry(lead.entity.industry)} />
                  <InfoRow label={t.leadDetail.jurisdiction} value={lead.entity.jurisdiction} />
                  {lead.entity.website && (
                    <InfoRow label={t.leadDetail.website} value={
                      <a href={`https://${lead.entity.website}`} target="_blank" rel="noopener noreferrer" className="text-reevol-blue hover:underline">
                        {lead.entity.website}
                      </a>
                    } />
                  )}
                </div>
                {/* Industry Relevance */}
                {lead.entity.industryRelevance && lead.entity.industryTags && lead.entity.industryTags.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-reevol-border/50">
                    <div className="flex items-center gap-2 mb-1.5">
                      <p className="text-xs font-medium text-reevol-text-muted uppercase tracking-wide">{t.leadDetail.industryRelevance}</p>
                      <IndustryRelevanceBadge relevance={lead.entity.industryRelevance} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {tr.industryTags(lead.entity.industryTags).map((tag, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-0.5 bg-reevol-indigo/10 text-reevol-indigo rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {lead.entity.geographicMarkets && lead.entity.geographicMarkets.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-reevol-border/50">
                    <p className="text-xs font-medium text-reevol-text-muted uppercase tracking-wide mb-1.5">{t.leadDetail.markets}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tr.markets(lead.entity.geographicMarkets).map((m, i) => (
                        <span key={i} className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full text-xs text-reevol-text-secondary">
                          <Globe size={10} /> {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Credit & Financial */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard size={16} className="text-reevol-text-muted" />
              <h2 className="text-sm font-semibold text-reevol-text">{t.leadDetail.creditFinancial}</h2>
            </div>
            <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <InfoRow label={t.leadDetail.creditRating} value={tr.creditRating(lead.credit.creditRating)} />
              <InfoRow label={t.leadDetail.creditLimit} value={lead.credit.creditLimitIndicator} />
            </div>
            {lead.credit.paymentBehavior && (
              <div className="mt-3 pt-3 border-t border-reevol-border/50">
                <p className="text-xs font-medium text-reevol-text-muted uppercase tracking-wide mb-1">{t.leadDetail.paymentBehavior}</p>
                <p className="text-sm text-reevol-text-secondary">{tr.payment(lead.credit.paymentBehavior)}</p>
              </div>
            )}
          </Card>

          {/* Risk Assessment */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert size={16} className="text-reevol-text-muted" />
              <h2 className="text-sm font-semibold text-reevol-text">{t.leadDetail.riskAssessment}</h2>
            </div>
            <div className="flex items-center gap-4 mb-3">
              <div>
                <p className="text-xs text-reevol-text-muted">{t.leadDetail.riskScore}</p>
                <p className="text-2xl font-bold text-reevol-text">{lead.risk.riskScore}</p>
              </div>
              <RiskBadge tier={lead.risk.riskTier} />
              <div className="ml-auto text-right">
                <p className="text-xs text-reevol-text-muted">{t.leadDetail.identityConfidence}</p>
                <p className="text-lg font-semibold text-reevol-text">{lead.risk.identityConfidence}%</p>
              </div>
            </div>
            {lead.risk.fraudFlags.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-reevol-border/50">
                <p className="text-xs font-medium text-reevol-text-muted uppercase tracking-wide">{t.leadDetail.fraudFlags}</p>
                {lead.risk.fraudFlags.map((flag) => {
                  const translated = tr.fraudFlag(flag);
                  return (
                    <div key={flag.id} className="flex items-start gap-2 text-sm">
                      <RiskBadge tier={flag.severity} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-reevol-text">{translated.name}</p>
                        <p className="text-xs text-reevol-text-muted">{translated.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Score */}
          <Card className="p-5 text-center">
            <LeadScoreBadge band={lead.scoreBand} score={lead.scoreNumeric} size="lg" />
            <p className="text-sm font-medium text-reevol-text mt-3">{t.leadDetail.leadScore}</p>
          </Card>

          {/* Recommended Action */}
          <div className="rounded-card p-5 bg-gradient-to-br from-reevol-blue to-reevol-cyan text-white">
            <div className="flex items-center gap-2 mb-2">
              <ChevronRight size={16} />
              <h2 className="text-sm font-semibold">{t.leadDetail.recommendedAction}</h2>
            </div>
            <p className="text-sm opacity-90 leading-relaxed">{lang === 'zh' && lead.recommendedActionZh ? lead.recommendedActionZh : lead.recommendedAction}</p>
          </div>

          {/* Notes */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-reevol-text mb-2">{t.leadDetail.notes}</h2>
            <textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setNotesEdited(true);
              }}
              placeholder={t.leadDetail.notesPlaceholder}
              rows={4}
              className="w-full px-3 py-2 bg-reevol-input border border-reevol-border rounded-xl text-sm text-reevol-text placeholder:text-reevol-text-muted focus:outline-none focus:ring-2 focus:ring-reevol-blue/30 resize-none"
            />
            {notesEdited && (
              <Button
                onClick={handleSaveNotes}
                loading={updateNotes.isPending}
                icon={<Save size={14} />}
                className="mt-2"
                fullWidth
              >
                {t.leadDetail.saveNotes}
              </Button>
            )}
          </Card>

          {/* New Lead */}
          <Button fullWidth onClick={() => navigate('/scan')}>
            {t.leadDetail.newLeadCheck}
          </Button>

          {/* Delete Lead */}
          <button
            onClick={handleDelete}
            disabled={deleteLead.isPending}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            <Trash2 size={14} />
            {deleteLead.isPending ? t.leadDetail.deleting : t.leadDetail.deleteLead}
          </button>
        </div>
      </div>

      {/* Advisory notice */}
      <p className="text-xs text-reevol-text-muted text-center mt-6">
        {t.common.advisory}
      </p>
    </div>
  );
}

function EditField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-xs text-reevol-text-muted">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-1.5 bg-reevol-input border border-reevol-border rounded-lg text-sm text-reevol-text focus:outline-none focus:ring-2 focus:ring-reevol-blue/30"
      />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs text-reevol-text-muted">{label}</p>
      <p className="text-reevol-text font-medium">{value}</p>
    </div>
  );
}

const RELEVANCE_STYLES: Record<string, string> = {
  high: 'bg-emerald-50 text-emerald-700',
  medium: 'bg-blue-50 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

function IndustryRelevanceBadge({ relevance }: { relevance: string }) {
  const { lang } = useLanguage();
  const tr = useTranslateContent(lang);
  const cls = RELEVANCE_STYLES[relevance] ?? RELEVANCE_STYLES.low;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {tr.relevance(relevance)}
    </span>
  );
}
